<template>
  <div class="tenant-billing-page">
    <header class="page-header">
      <div>
        <p class="eyebrow">BILLING</p>
        <h1>請求・お支払い</h1>
        <p>
          今月のご請求額、過去のご請求履歴、お支払い方法の設定です。
        </p>
      </div>
      <CButton
        color="secondary"
        variant="outline"
        :disabled="loading || saving"
        @click="load"
      >
        再読込
      </CButton>
    </header>

    <!-- 上端に出すのは読み込みの失敗だけ。保存の結果は［保存する］のすぐ下に出す。 -->
    <CAlert v-if="errorMessage" color="danger">{{ errorMessage }}</CAlert>

    <CRow>
      <CCol md="5">
        <CCard class="current-card">
          <CCardHeader>
            今月（{{ readableMonth(current.yearMonth) }}）のご請求
            <CBadge
              v-if="!showsBillingExempt"
              :color="current.source === 'FIXED' ? 'primary' : 'secondary'"
            >
              {{ current.source === 'FIXED' ? '確定' : '見込み' }}
            </CBadge>
          </CCardHeader>
          <CCardBody>
            <!--
              **請求の対象外の会社（運営会社）には、「今月のご請求はありません。」だけを出す**
              （2026-10-08、実機で発見）。

              右の欄は「利用料の請求は発生しません」の1文だけなのに、こちらには札・
              「翌月1日に確定します…」・大きい字の「集計中」・人数の数え方の説明が並び、
              請求があるかのように読めた。出すのは、請求の対象でない月に前から出している
              文と同じ1文。条件は右の欄と同じ1つ（showsBillingExempt）。

              **ほかの会社の見え方は変えない。**下の枝は囲んだだけで、中は前のまま。
            -->
            <p
              v-if="showsBillingExempt"
              class="billing-exempt"
              data-testid="tenant-billing-current-exempt"
            >{{ noBillingThisMonthNote }}</p>
            <template v-else>
            <!--
              **見込みが0円のときは、「翌月1日に確定します。」と言わない**（2026-10-10、Gemini J5）。
              料金が0円で登録されている月は、運営の側で確定が見送られる（0円の請求は立てない）。
              前は ¥0 の上に「翌月1日に確定します。…」と出て、0円で確定するように読めた。
              無料プラン・初期特別価格で0円・運営会社は「今月のご請求はありません。」の側で、
              ここには来ない。金額を出せない月（集計中）は今までどおり下の文。
            -->
            <p
              v-if="current.source !== 'FIXED' && estimateIsZeroYen"
              class="note"
              data-testid="tenant-billing-zero-estimate"
            >{{ zeroEstimateNote }}</p>
            <!--
              **料金は入っているが、計算の結果が0円で確定が見送られる月**（2026-10-10、監査 BF-5、
              Gemini P1）。前は「翌月1日に確定します。…」と出ていた。サーバは見送るのに、
              画面は確定すると言い切っていた。金額（¥0）と内訳は、そのまま下に出す
              （「未設定」にはしない）。
            -->
            <p
              v-else-if="current.source !== 'FIXED' && estimateWillBeHeld"
              class="note"
              data-testid="tenant-billing-held-estimate"
            >{{ heldEstimateNote }}</p>
            <p v-else-if="current.source !== 'FIXED'" class="note">
              <!--
                **見込みだと分かるように書く。**確定と同じ顔で出すと、
                月末に金額が動いたときに「話が違う」になる。
              -->
              <!--
                **確定は翌月の1日。**その月に一度でも登録されていた
                方の人数で計算するので、月が終わらないと決まらない。
              -->
              翌月1日に確定します。確定前は<strong>いまの時点の見込み額</strong>で、
              月の途中で人数が増えると上がります。
            </p>

            <!--
              **人数が分からない月に、0円の見込みを見せない。**
              基本料だけの安い額が出ると、お客様は「今月はこの額だ」と
              読む。実際の請求と食い違ったときに説明できない
              （2026-09-19、監査 B5）。
            -->
            <template v-if="current.estimateUnavailable">
              <div class="amount amount--unavailable">集計中</div>
              <p class="amount-note">
                {{ current.estimateUnavailableReason }}
              </p>
            </template>
            <!--
              **見込みが0円で、料金の設定が済んでいない月は、金額の代わりに「未設定」と出す**
              （2026-10-10、Gemini K4）。上の説明は「金額がまだ決まっていません」と言うので、
              大きい字の ¥0 と並ぶと食い違う。「税込」も出さない（金額ではないため）。
            -->
            <template v-else-if="estimateIsZeroYen">
              <div class="amount amount--unset" data-testid="tenant-billing-amount-unset">{{ amountUnsetLabel }}</div>
            </template>
            <template v-else>
              <div class="amount">{{ yen(current.totalAmount) }}</div>
              <div class="amount-note">税込</div>
              <!--
                **集計がそろっていない月は、そうと書く。**

                金額を止めてしまうと、月の途中に見たい方が誰も
                見られない。かといって黙って出すと、**その額で
                支払いの用意をされる**（2026-09-19、監査 R3）。
              -->
              <p v-if="current.estimateIsProvisional" class="note">
                {{ current.estimateProvisionalReason }}
              </p>
            </template>

            <dl v-if="!current.estimateUnavailable" class="breakdown">
              <dt>プラン</dt>
              <dd>
                {{ current.planName || '—' }}
                <!--
                  初期特別価格（0100）。**なぜ通常より安いのかを、その場で分かるように。**
                  金額は下の基本料・1人あたりに、特別価格のまま出ている。
                -->
                <span v-if="current.introApplied" class="intro-badge">
                  初期特別価格（{{ current.introMonthIndex }}か月目／{{ current.introMonths }}か月）
                </span>
              </dd>

              <!--
                **料金の設定が済んでいない月（見込みが0円）は、金額の行を出さない**（2026-10-10）。
                上で「金額がまだ決まっていません」「未設定」と言うので、下に ¥0 が並ぶと
                食い違う。残すのは、プランの名前と、ご利用人数（人数だけ。単価の掛け算は出さない）。
                出さないのは、基本料・人数の横の「× 単価」・帯ごとの行・最低月額調整額・小計・消費税。
                金額が決まっている月・確定済みの月は今までどおり全部出す。
              -->
              <template v-if="!estimateIsZeroYen">
                <dt>基本料</dt>
                <dd class="numeric">{{ yen(current.basePrice) }}</dd>
              </template>

              <dt>ご利用人数</dt>
              <dd class="numeric">
                {{ current.billedUserCount }} 人
                <!--
                  **ボリュームディスカウント（0108）を当てた月は、単価を1つにできない。**
                  人数だけを出し、下に帯ごとの行を並べる（帯の人数 × 帯の単価）。
                -->
                <span v-if="!hasVolumeDiscountTiers && !estimateIsZeroYen" class="muted">× {{ yen(current.pricePerUser) }}</span>
              </dd>

              <!--
                **帯ごとの行（0108）。**割引を当てた月だけ。請求書の明細と同じ品目の書き方で、
                額は帯の金額、添えに「人数 × 単価」。足すと「人数ぶんの額」になり、
                基本料・最低月額調整額と合わせて小計に一致する。
                品目の（{月}分・{範囲}）は途中で折り返さない（折り返すなら全角の空白の所で）。
              -->
              <template v-if="hasVolumeDiscountTiers && !estimateIsZeroYen">
                <template v-for="tier in volumeDiscountTiers">
                  <dt :key="'tier-term-' + tier.fromSeat" class="tier-term">
                    {{ tierItemName }}<span class="tier-term__range">（{{ readableMonth(current.yearMonth) }}分・{{ tierRange(tier) }}）</span>
                  </dt>
                  <dd :key="'tier-amount-' + tier.fromSeat" class="numeric tier-amount">
                    {{ yen(tier.amount) }}
                    <span class="muted">{{ seatCount(tier.seats) }} 人 × {{ yen(tier.pricePerUser) }}</span>
                  </dd>
                </template>
                <!--
                  **参考。合計には含めない**（帯の行が割引後の額なので、引くと二重になる）。
                  いくら割り引かれたかを、その場で分かるように。
                -->
                <dd class="volume-discount-aside">人数割適用額：-{{ discountAmountText(current.volumeDiscountAmount) }}円</dd>
              </template>

              <!--
                **最低月額との差額（0107）。**月額が最低月額に届かない月は、
                差額を1行で足す。人数と単価は実際のまま出す（書き換えない）。
                **無いと「基本料＋人数×単価」が小計と合わない。**差額が0の月は出さない。
              -->
              <template v-if="hasMinimumAdjustment && !estimateIsZeroYen">
                <dt>最低月額調整額</dt>
                <dd class="numeric">{{ yen(current.minimumAdjustmentAmount) }}</dd>
              </template>

              <template v-if="!estimateIsZeroYen">
                <dt>小計</dt>
                <dd class="numeric">{{ yen(current.subtotalAmount) }}</dd>
                <dt>消費税（{{ current.taxRate }}%）</dt>
                <dd class="numeric">{{ yen(current.taxAmount) }}</dd>
              </template>

              <template v-if="current.source === 'FIXED'">
                <dt>ご入金</dt>
                <dd class="numeric">{{ yen(current.paidAmount) }}</dd>
              </template>
            </dl>

            <!--
              **なぜ実際の人数ではないのかを、その場で書く。**
              問い合わせがいちばん来るのはここ。

              **前は「増減しても変わりません」と書いていた。誤り。**
              すぐ上の見込みの注記が「人数が増えると上がります」なので、
              同じ画面で真逆のことを言っていた（2026-09-19、実機で発見）。
              その月に一度でも登録されていた方を数えるのだから、
              **増えれば上がる。減っても下がらない。**
              日割りをしないというのは「入った日・辞めた日で按分しない」
              という意味であって、「人数が変わっても動かない」ではない。
            -->
            <p class="note">
              その月に一度でも登録されていた方の人数で計算しています。月の途中で入られた方も、退職された方も、その月は1人として数えます。日割りの計算はありません。
              月の途中で人数が増えるとご請求額は上がりますが、退職された方が出てもその月は下がりません。
            </p>
            <!--
              **最低月額（0107）が当たる月だけ出す。**当たる月は、最低月額に届くまで
              人数が増えても請求額は上がらない。すぐ上の「増えると上がります」を
              補う。当たらない会社（当てない設定・初期特別価格の期間など）には出さない。
            -->
            <p v-if="hasMinimumMonthlyAmount" class="note">ご利用額が最低月額に届かない月は最低月額となり、その範囲内では人数が増えてもご請求額は変わりません。</p>
            </template>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol md="7">
        <CCard>
          <CCardHeader>お支払い方法とご請求先</CCardHeader>
          <CCardBody>
            <!--
              **請求の対象外の会社（運営の会社）には、説明の1文だけを出す**（2026-10-08、9/30 の決め1）。

              運営の会社には請求を立てない。前は、ほかの「選べる支払方法が無い会社」と同じく
              「請求書払い（銀行振込）」と「毎月発行される請求書は…お振込みください」が出て、
              同じ画面の「今月のご請求はありません。」と食い違っていた。請求書を出さないので、
              お支払い方法の値も、ご請求先（名義・メール・住所）の欄も、［保存する］も出さない。
              どの会社が対象外かは back が決めて返す（billingExempt。ここで判定しない）。

              **読めてから出す。**読めていないあいだは下の枝（入力も保存も止めた欄）のまま。
            -->
            <p
              v-if="showsBillingExempt"
              class="billing-exempt"
              data-testid="tenant-billing-exempt"
            >{{ billingExemptNote }}</p>
            <template v-else>
            <!--
              **お支払い方法を選べない会社には、いまのお支払い方法を「選べない値」として見せる**
              （2026-10-08。利用者「支払い方法を設定する画面もないのに何このエラー？」）。

              前は「現在ご利用いただけるお支払い方法がありません。PREIKA運営窓口までご連絡ください。」
              と出し、［保存する］も押せなかった。ところが請求は、保存の無い会社を請求書払いとして
              確定している。請求書は出るのに、その宛名・送り先・住所を入れる手段が無かった
              （本番は全プランで支払方法の機能が許可されていない＝全社がこの状態）。

              見せる値は、保存済みの支払方法（無ければ確定の既定と同じ請求書払い）。
              説明（TB-1、Gemini）は請求書払いのときだけ添える。

              **選べる方法が1つだけのときも、選ぶ欄にしない**（2026-10-08）。運営が請求書払いを
              許可した会社では、選択肢が「請求書払い（銀行振込）」1つだけの選ぶ欄が出ていた。
              開いても何も選べず、ヘルプの「固定されており、選択・変更することはできません」とも
              合わなかった。1つ以下なら値として見せ、2つ以上のときだけ選ぶ欄にする。
              1つのときに見せるのは、その1つ（保存のときに送る値と同じ。selectableOnlyMethod）。
            -->
            <div
              v-if="settingLoaded && !offersChoice"
              class="form-group fixed-method"
              data-testid="tenant-billing-fixed-method"
            >
              <label>お支払い方法</label>
              <div class="fixed-method-value">{{ fixedMethodLabel }}</div>
              <small v-if="fixedMethodIsInvoice" class="form-text text-muted">毎月発行される請求書は、「これまでのご請求」の［請求書 PDF］から確認いただけます。記載された振込先へ期日までにお振込みください。</small>
            </div>

            <CSelect
              v-else-if="settingLoaded"
              label="お支払い方法"
              :value.sync="form.paymentMethod"
              :options="methodOptions"
              :disabled="saving || !settingLoaded"
            />

            <CInput
              label="ご請求先の名義"
              v-model="form.billingName"
              :disabled="saving || !settingLoaded"
            />
            <CInput
              label="ご請求先のメールアドレス"
              type="email"
              v-model="form.billingEmail"
              :disabled="saving || !settingLoaded"
              description="請求書の発行をこちらへお知らせします。"
            />
            <CTextarea
              label="ご請求先の住所"
              v-model="form.billingAddress"
              rows="3"
              :disabled="saving || !settingLoaded"
            />

            <!--
              **お支払い方法を選べない会社でも、ご請求先は保存できる**（2026-10-08）。
              前はここに「お支払い方法が未設定のため保存できません。…運営窓口まで」と出して
              ボタンを押せなくしていた（2026-09-09 にテスターが「全項目を入力したのに押せない」と
              書いたとき、押せるようにせず理由の文を足しただけだった）。
            -->
            <!--
              **設定を読めなかったときは、入力も保存も止める**（監査 BSET-1、2026-10-08）。
              ［保存する］を「選べる支払方法が無くても押せる」ようにしたので、読めていない（欄が
              空の初期値のまま）ときまで押せると、空の欄で保存して、保存済みのメールや住所を消す。
              「選べる支払方法が無い」と「まだ読めていない」は別の状態として持つ（settingLoaded）。
              押せない理由はボタンのそばに出す（TB-3、Gemini）。読み込み中は出さない（すぐ終わる）。
            -->
            <p v-if="settingFailed" class="save-blocked" data-testid="tenant-billing-setting-failed">ご請求先の情報を読み込めなかったため、現在はお手続きできません。画面を再読み込みしてください。</p>
            <CButton
              color="primary"
              :disabled="saving || !settingLoaded"
              @click="save"
            >
              保存する
            </CButton>
            <ActionResultMessage :text="saveMessage.text" :type="saveMessage.type" />
            </template>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <CCard>
      <CCardHeader>これまでのご請求</CCardHeader>
      <CCardBody>
        <CDataTable
          :items="history"
          :fields="historyFields"
          :loading="loading"
          hover
          class="billing-history-table"
        >
          <template #yearMonth="{ item }">
            <td>{{ readableMonth(item.yearMonth) }}</td>
          </template>
          <template #billedUserCount="{ item }">
            <td class="numeric">{{ item.billedUserCount }} 人</td>
          </template>
          <!--
            **割引額の写し（0108）がある月だけ、金額の下に V33 を添える**（点検 6）。
            確定したときの額（写し）で、今の設定から計算し直さない。**合計には含めない**
            （ご請求額はもう割引後の額。今月のご請求の参考と同じ書き方）。
          -->
          <template #totalAmount="{ item }">
            <td class="numeric">
              {{ yen(item.totalAmount) }}
              <div v-if="hasHistoryDiscount(item)" class="history-discount">人数割適用額：-{{ discountAmountText(item.volumeDiscountAmount) }}円</div>
            </td>
          </template>
          <template #billingStatus="{ item }">
            <td>
              <CBadge :color="statusColor(item.billingStatus)">
                {{ statusLabel(item.billingStatus) }}
              </CBadge>
            </td>
          </template>
          <!--
            **問い合わせのときに言う番号。**
            出していないと、電話口で「何月分の」から始まる。
          -->
          <template #invoiceNo="{ item }">
            <td>
              <span v-if="item.invoiceNo">{{ item.invoiceNo }}</span>
              <span v-else class="muted">—</span>
            </td>
          </template>
          <template #dueDate="{ item }">
            <td>{{ day(item.dueDate) || '—' }}</td>
          </template>
          <!--
            **請求書 PDF（2026-09-22）。**発行済み（番号が付いた）行だけ。
            大手（KING OF TIME・freee・マネーフォワード・kintone・ジョブカン）は
            お客様の画面から請求書を取れる。PREIKA はメールの添付だけだった。
          -->
          <!--
            **帯ごとの行（0108）。割引を当てた月だけ、その行のすぐ下に出す**（今月のご請求と同じ形：
            V32「{プラン名}（全角の空白）ご利用料（{月}分・{範囲}）」、額は帯の金額、添えに「人数 × 単価」）。
            back は各行に確定の写しの帯（volumeDiscountTiers）を返す。割引の無かった月は [] で、
            ここは何も描かない（今までどおり1行）。今の設定から計算し直さない。
            表の縞（striped）は外した：この差し込み行が1行おきに入るので、縞が全部の行に同じ色で付く。
          -->
          <template #details="{ item }">
            <dl v-if="historyTiers(item).length" class="history-tiers">
              <template v-for="tier in historyTiers(item)">
                <dt :key="'history-tier-term-' + item.id + '-' + tier.fromSeat" class="tier-term">
                  {{ historyTierItemName(item) }}<span class="tier-term__range">（{{ readableMonth(item.yearMonth) }}分・{{ tierRange(tier) }}）</span>
                </dt>
                <dd :key="'history-tier-amount-' + item.id + '-' + tier.fromSeat" class="numeric tier-amount">
                  {{ yen(tier.amount) }}
                  <span class="muted">{{ seatCount(tier.seats) }} 人 × {{ yen(tier.pricePerUser) }}</span>
                </dd>
              </template>
            </dl>
          </template>
          <template #invoicePdf="{ item }">
            <td>
              <a
                v-if="item.invoiceNo"
                :href="invoicePdfUrl(item)"
                target="_blank"
                rel="noopener"
                class="btn btn-sm btn-outline-primary"
              >請求書 PDF</a>
              <span v-else class="muted">—</span>
            </td>
          </template>
        </CDataTable>

        <p v-if="!loading && !history.length" class="note">
          まだご請求の履歴がありません。
        </p>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import tenantBilling from '@/services/tenantBilling'
import ActionResultMessage from '@/components/ActionResultMessage.vue'
import { amountText, rangeText, seatCountText } from '@/utils/volumeDiscount'
import { BRAND } from '@/common/productName'

/*
 * お支払い方法の呼び名。
 *
 * **口座振替とクレジットカードは、いまは画面に出ない**（2026-10-08、9/30 の決め3）。
 * 選べる方法（availableMethods）にも、いまの方法（paymentMethod）にも、back が
 * 「決済の準備が済んだ方法」だけを返す——口座振替で保存してある会社にも、実際に
 * 請求に使う請求書払いを返す。どの方法が準備済みかは back の1か所が決める
 * （ここに同じ判定を書かない）。呼び名を残してあるのは、つないだ日に back の
 * 1か所を直すだけで出せるようにするため。
 */
const METHOD_LABEL = {
  BANK_TRANSFER: '口座振替',
  INVOICE: '請求書払い（銀行振込）',
  CARD: 'クレジットカード'
}

/*
 * 請求の対象外の会社（運営の会社）が開いたときに、「お支払い方法とご請求先」の欄に出す文。
 * 文は Gemini と決めた OW-10（2026-10-08、9/30 の決め1）。
 * シリーズの名前は1か所（productName.js）から取る（運営の話なので、製品名ではなく BRAND）。
 */
const BILLING_EXEMPT_NOTE = BRAND + ' を運営している会社のため、利用料の請求は発生しません。お支払い方法やご請求先の登録は不要です。'

/*
 * 請求の対象外の会社（運営会社）の「今月のご請求」に出す1文（2026-10-08）。
 * 請求の対象でない月（試用中・解約後）に、前から出している文と同じ（そちらは back が
 * 今月の請求と一緒に返す）。運営会社には、今月の応答の中身に関わらずこの1文だけを出す。
 */
const NO_BILLING_THIS_MONTH_NOTE = '今月のご請求はありません。'

/*
 * 見込み額が0円のときの説明（2026-10-10、Gemini J5。画面に出る字は一字も変えない）。
 * シリーズの名前は1か所（productName.js）から取る（運営の話なので、製品名ではなく BRAND）。
 */
/*
 * 料金は入っているが、計算の結果が0円で確定が見送られる月の説明（2026-10-10、Gemini P1。
 * 画面に出る字は一字も変えない）。シリーズの名前は1か所（productName.js）から取る。
 */
const HELD_ESTIMATE_NOTE = '現在の設定ではご請求が発生しないため、確定および請求書の発行は行われません。内容について' + BRAND + 'の担当より確認のご連絡を差し上げる場合がございます。'

/* 見込み額が0円のとき、大きい字の金額の代わりに出す語（2026-10-10、Gemini K4）。 */
const AMOUNT_UNSET_LABEL = '未設定'

const ZERO_ESTIMATE_NOTE = '料金の設定が済んでいないため、金額がまだ決まっていません。お手数ですが、' + BRAND + ' の担当へご連絡ください。'

const STATUS_LABEL = {
  DRAFT: '見込み',
  FIXED: '確定',
  INVOICED: '請求書発行済み',
  PAID: 'お支払い済み',
  VOID: '取消'
}

const STATUS_COLOR = {
  DRAFT: 'secondary',
  FIXED: 'info',
  INVOICED: 'primary',
  PAID: 'success',
  VOID: 'dark'
}

export default {
  name: 'TenantBilling',

  components: { ActionResultMessage },

  data () {
    return {
      current: {},
      history: [],
      availableMethods: [],
      form: {
        paymentMethod: '',
        billingName: '',
        billingEmail: '',
        billingAddress: ''
      },
      loading: false,
      saving: false,
      /* 保存済みのお支払い方法（無ければ空文字）。選べない会社に見せる値に使う。 */
      savedMethod: '',
      /*
       * 請求の対象外の会社か（運営の会社）。back が返した印。**true と明示されたときだけ。**
       * 読めていない・項目が無いときは false（ふつうの会社の欄のまま）。
       */
      billingExempt: false,
      /*
       * お支払い方法とご請求先を読めたか。**読めるまでは入力も保存もさせない。**
       * 読めた結果が「選べる方法なし・請求先なし」のときは true（保存できる）。
       */
      settingLoaded: false,
      /* 読もうとして失敗した（読み込み中は false）。押せない理由を出すのに使う。 */
      settingFailed: false,
      // 読み込みの失敗だけ（画面の上端に出す）。
      errorMessage: '',
      // 保存の結果。［保存する］のすぐ下に出す（上端はボタンの約155行上）。
      saveMessage: { type: 'success', text: '' },
      historyFields: [
        { key: 'yearMonth', label: 'ご請求月' },
        { key: 'billedUserCount', label: 'ご利用人数' },
        { key: 'totalAmount', label: 'ご請求額（税込）' },
        { key: 'billingStatus', label: '状態' },
        { key: 'invoiceNo', label: '請求書番号' },
        { key: 'dueDate', label: 'お支払期限' },
        { key: 'invoicePdf', label: '請求書' }
      ]
    }
  },

  computed: {
    /** 選ぶ欄を出すか。**選べる方法が2つ以上のときだけ**（1つだけの選ぶ欄は、開いても何も選べない）。 */
    offersChoice () {
      return this.availableMethods.length > 1
    },

    /** 選べる方法がちょうど1つのときの、その方法。0件・2つ以上なら空文字。 */
    selectableOnlyMethod () {
      return this.availableMethods.length === 1 ? this.availableMethods[0] : ''
    },

    /**
     * 選ぶ欄を出さない会社に見せる値。
     * 選べる方法が1つならその1つ（保存で送る値と同じ）。0件なら、保存済みの方法、無ければ
     * 確定の既定と同じ請求書払い。
     */
    fixedMethodCode () {
      return this.selectableOnlyMethod || this.savedMethod || 'INVOICE'
    },

    /**
     * 保存のときに送るお支払い方法。
     *   0件 … 空（back は支払方法を変えずに、ご請求先だけを保存する）
     *   1つ … その1つ（前に口座振替で保存してある会社を、保存で請求書払いへ直す）
     *   2つ以上 … 選ぶ欄の値
     */
    methodToSend () {
      if (!this.availableMethods.length) return ''
      return this.selectableOnlyMethod || this.form.paymentMethod
    },
    fixedMethodLabel () {
      return METHOD_LABEL[this.fixedMethodCode] || this.fixedMethodCode
    },
    fixedMethodIsInvoice () {
      return this.fixedMethodCode === 'INVOICE'
    },

    /** 請求の対象外の会社に出す1文。 */
    billingExemptNote () {
      return BILLING_EXEMPT_NOTE
    },

    /**
     * 請求の対象外の会社として見せるか。**読めてから**（読み直しのあいだは、ふつうの欄のまま）。
     * 左の欄（今月のご請求）と右の欄（お支払い方法とご請求先）で、同じ条件を使う。
     */
    showsBillingExempt () {
      return this.settingLoaded && this.billingExempt
    },

    /** 請求の対象外の会社の「今月のご請求」に出す1文。 */
    zeroEstimateNote () {
      return ZERO_ESTIMATE_NOTE
    },

    amountUnsetLabel () {
      return AMOUNT_UNSET_LABEL
    },

    heldEstimateNote () {
      return HELD_ESTIMATE_NOTE
    },

    /**
     * 見込みの月で、いまの条件のままだと確定が見送られるか（料金が未設定の月は別に扱う）。
     * サーバが返す真偽（willBeHeld）に従う。合計が0円かどうかでは決めない。
     * 確定済みの月・金額を出せない月（集計中）は false。
     */
    estimateWillBeHeld () {
      const current = this.current || {}
      if (current.source === 'FIXED' || current.estimateUnavailable) return false
      return current.willBeHeld === true && current.priceUnset !== true
    },

    /**
     * 見込みの月で、料金の設定が済んでいないか（2026-10-10）。
     *
     * **合計が0円かどうかでは決めない**（監査 BF-2）。料金は入っているのに人数割などで
     * 0円になる月まで「料金の設定が済んでいない」と案内してしまうため。サーバが返す
     * 状態（priceUnset。確定で見送る理由と同じ決まり）に従う。
     * 確定済みの月・金額を出せない月（集計中）は false。
     */
    estimateIsZeroYen () {
      const current = this.current || {}
      if (current.source === 'FIXED' || current.estimateUnavailable) return false
      return current.priceUnset === true
    },

    noBillingThisMonthNote () {
      return NO_BILLING_THIS_MONTH_NOTE
    },

    methodOptions () {
      return this.availableMethods.map(value => ({
        value,
        label: METHOD_LABEL[value] || value
      }))
    },

    /**
     * ボリュームディスカウントの帯ごとの内訳（0108）。back は割引の無い月は [] を返す
     * （確定済みの月は確定したときの写し）。人数0の帯は back が返さない。
     */
    volumeDiscountTiers () {
      return Array.isArray(this.current.volumeDiscountTiers) ? this.current.volumeDiscountTiers : []
    },

    /**
     * 帯の行の品目の前半（V32 の「{プラン名}」＋全角の空白＋「ご利用料」。後ろに（{月}分・{範囲}）が続く）。
     * プラン名とのあいだは全角の空白（請求書 PDF と同じ）。テンプレートに全角の空白を直に書かない
     * （見えないので消えても気づけない。eslint も断る）。
     */
    tierItemName () {
      return (this.current.planName || '') + String.fromCharCode(0x3000) + 'ご利用料'
    },

    /** 帯ごとの行を出す月か。**割引の無い月は今までどおり**（人数 × 単価の1行）。 */
    hasVolumeDiscountTiers () {
      return this.volumeDiscountTiers.length > 0
    },

    /** 最低月額との差額があるか（0107）。**0円・無い月は行を出さない。** */
    hasMinimumAdjustment () {
      return Number(this.current.minimumAdjustmentAmount || 0) > 0
    },

    /**
     * この月に最低月額が当たるか（0107）。back は当てた月だけ金額を返し、
     * 当てない月は null（あるいは鍵ごと無い）。
     */
    hasMinimumMonthlyAmount () {
      const value = this.current.minimumMonthlyAmount
      return value !== null && value !== undefined && value !== ''
    }
  },

  created () {
    this.load()
  },

  methods: {
    invoicePdfUrl (item) {
      return tenantBilling.invoicePdfUrl(item.id)
    },

    load () {
      this.loading = true
      this.errorMessage = ''
      this.settingLoaded = false
      this.settingFailed = false
      /*
       * **お支払い方法とご請求先は、今月の請求・過去の請求とは別に読む**（監査 BSET-1）。
       * 前は3本をまとめて待ち、1本でも落ちると請求先を欄へ入れなかった。今月の請求だけが
       * 落ちても、請求先は読めているなら出して、直せるようにする。逆に請求先が読めなかった
       * ときは、欄も［保存する］も止める（空のまま保存すると、保存済みの値を消す）。
       */
      const bills = Promise.all([
        tenantBilling.current(),
        tenantBilling.history(12)
      ])
        .then(([current, history]) => {
          this.current = current || {}
          this.history = Array.isArray(history) ? history : []
        })
        .catch(error => { this.errorMessage = this.messageOf(error) })
      const setting = tenantBilling.setting()
        .then(loaded => {
          // 形が違う応答（選べる方法の並びが無い）は、読めたことにしない。
          if (!loaded || !Array.isArray(loaded.availableMethods)) {
            this.settingFailed = true
            return
          }
          this.applySetting(loaded)
          this.settingLoaded = true
        })
        .catch(error => {
          this.settingFailed = true
          this.errorMessage = this.messageOf(error)
        })
      Promise.all([bills, setting]).then(() => { this.loading = false })
    },

    applySetting (setting) {
      this.availableMethods = Array.isArray(setting.availableMethods)
        ? setting.availableMethods
        : []
      /*
       * **まだ保存していない会社は、確定と同じ「請求書払い」を選んだ形で出す**
       * （2026-09-30 の洗い出し、請求 #1）。
       *
       * 確定は保存が無ければ請求書払いとして扱う（BillingLifecycleMapper の
       * COALESCE(setting.payment_method, 'INVOICE')）。前は選択肢の先頭（口座振替）を
       * 出していたので、画面は口座振替・請求は請求書払いと食い違い、名義だけ直して
       * ［保存する］を押すと、触っていない支払方法が口座振替で保存されて支払期限が
       * 約1か月早まった。
       *
       * 選べる方法に請求書払いが無いときは先頭を出す。いま back が返す選べる方法は
       * 請求書払いだけ（口座振替とカードは、つなぐまで返さない。2026-10-08）なので、
       * この枝を通る会社は無い。
       *
       * **保存してある会社の値も、back が「請求に使う方法」に決め直して返す**
       * （口座振替で保存してある会社には請求書払い）。画面は受け取った値をそのまま出す。
       */
      const unsavedDefault = this.availableMethods.indexOf('INVOICE') >= 0
        ? 'INVOICE'
        : (this.availableMethods.length ? this.availableMethods[0] : '')
      this.savedMethod = setting.paymentMethod || ''
      this.billingExempt = setting.billingExempt === true
      this.form = {
        paymentMethod: setting.paymentMethod || unsavedDefault,
        billingName: setting.billingName || '',
        billingEmail: setting.billingEmail || '',
        billingAddress: setting.billingAddress || ''
      }
    },

    save () {
      // 読めていない請求先を、空の欄で上書きしない（ボタンは押せないが、ここでも止める）。
      if (!this.settingLoaded) return
      // 請求の対象外の会社には欄も［保存する］も出していない。直に呼ばれても送らない。
      if (this.billingExempt) return
      this.saving = true
      this.saveMessage = { type: 'success', text: '' }
      tenantBilling.saveSetting({
        // 選べる方法が無い会社は送らない。1つならその1つ、2つ以上なら選んだ方法（methodToSend）。
        paymentMethod: this.methodToSend,
        billingName: this.form.billingName,
        billingEmail: this.form.billingEmail,
        billingAddress: this.form.billingAddress
      })
        .then(() => {
          this.saveMessage = { type: 'success', text: 'お支払い方法とご請求先を保存しました。' }
          // 読み直しの失敗は保存の失敗ではない。読み込みの失敗として上端に出す。
          // 形が違う応答では欄を入れ替えない（空の初期値に戻すと、次の保存で保存済みの値を消す）。
          return tenantBilling.setting()
            .then(s => { if (s && Array.isArray(s.availableMethods)) this.applySetting(s) })
            .catch(error => { this.errorMessage = this.messageOf(error) })
        })
        // 保存の失敗に「取得できませんでした」を出さない（読み込みの文言だった）。
        .catch(error => { this.saveMessage = { type: 'danger', text: this.messageOf(error, '保存できませんでした。時間をおいてもう一度お試しください。') } })
        .then(() => { this.saving = false })
    },

    readableMonth (value) {
      const text = String(value || '')
      if (text.length !== 6) return text || '—'
      return text.slice(0, 4) + '年' + Number(text.slice(4)) + '月'
    },

    statusLabel (value) {
      return STATUS_LABEL[value] || value
    },

    statusColor (value) {
      return STATUS_COLOR[value] || 'secondary'
    },

    yen (value) {
      return '¥' + Number(value || 0).toLocaleString('ja-JP')
    },

    /** 3桁区切りの数（「…適用額：-2,000円」の形。¥ を付けない）。 */
    discountAmountText (value) {
      return amountText(value || 0)
    },

    /** 帯の範囲（V32）：上限のある帯は「11〜30名」、最後の帯は「101名〜」（3桁区切り「1,001名〜」）。 */
    tierRange (tier) {
      return rangeText(tier.fromSeat, tier.toSeat)
    },

    /** 帯の行の人数（3桁区切り。請求書 PDF と同じ）。 */
    seatCount (value) {
      return seatCountText(value || 0)
    },

    /**
     * これまでのご請求の行の、帯ごとの内訳（0108。確定の写し）。割引の無かった月は []。
     * back が並びを返さない（古い口・割引の無い月）ときも [] にして、行を今までどおりにする。
     */
    historyTiers (item) {
      return item && Array.isArray(item.volumeDiscountTiers) ? item.volumeDiscountTiers : []
    },

    /**
     * これまでのご請求の帯の行の品目の前半（V32。今月のご請求の tierItemName と同じ組み方）。
     * プラン名はその請求の行のもの（item.planName）。**今のプラン名を借りない**（プランを替えた
     * 会社で、前の月の行に今のプラン名が出る）。行にプラン名が無いときは「ご利用料」から始める。
     */
    historyTierItemName (item) {
      const planName = item && item.planName ? String(item.planName) : ''
      return (planName ? planName + String.fromCharCode(0x3000) : '') + 'ご利用料'
    },

    /** これまでのご請求の行に、割引額の写しがあるか（0108）。無い・0円は添えない。 */
    hasHistoryDiscount (item) {
      return Number((item && item.volumeDiscountAmount) || 0) > 0
    },

    day (value) {
      return String(value || '').slice(0, 10)
    },

    messageOf (error, fallback) {
      const data = error && error.response ? error.response.data : null
      if (data && data.message) return data.message
      return fallback || 'ご請求の情報を取得できませんでした。時間をおいて画面を再読み込みしてください。'
    }
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

/* スマホでは見出しの説明を全幅にする（請求管理と同じ。右のボタンに押されて縮まないように）。 */
@media (max-width: 767.98px) {
  .page-header {
    flex-direction: column;
  }
}

.eyebrow {
  font-size: 0.75rem;
  letter-spacing: 0.12em;
  color: #768192;
  margin-bottom: 0.15rem;
}

.amount {
  font-size: 2rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
}

/* 金額ではないので、数字ほど強く出さない。 */
.amount--unavailable { color: #768192; font-size: 1.4rem; }
/* 料金の設定が済んでいない月の「未設定」。金額の字ではないので、集計中と同じ大きさと色。 */
.amount--unset { color: #768192; font-size: 1.4rem; }
.amount-note {
  font-size: 0.8rem;
  color: #768192;
  margin-bottom: 1rem;
}

.breakdown {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.35rem 1rem;
  margin-bottom: 1rem;
}

.breakdown dt {
  font-weight: 500;
  color: #768192;
}

.breakdown dd {
  margin-bottom: 0;
}

/*
  ボリュームディスカウントの帯の品目（0108）。「スタンダード（全角の空白）ご利用料（2026年10月分・11〜30名）」は
  長いので、見出しの列を広げすぎない幅で折り返す（金額の列を押しつぶさない）。
  **括弧の中では切らない**（切るなら全角の空白の所）。
*/
.breakdown dt.tier-term {
  max-width: 15rem;
}

.tier-term__range {
  display: inline-block;
}

/* 帯の行の添え（人数 × 単価）は、金額の下へ回ってもよい。 */
.tier-amount .muted {
  display: inline-block;
}

/* 割引額の参考。**合計に入る行と見分けがつくよう、2列にまたがる小さな注記にする。** */
.breakdown dd.volume-discount-aside {
  grid-column: 1 / -1;
  text-align: right;
  font-size: 0.8rem;
  color: #768192;
}

.numeric {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

/*
  これまでのご請求の帯ごとの行（0108）。その月の行のすぐ下に、今月のご請求の内訳と同じ2列で。
  品目の列は広げすぎない（15rem で折り返す。括弧の中では切らない）。金額は右寄せ。
*/
/*
  「これまでのご請求」の、行の下の差し込みのセル（2026-10-10）。差し込みの行は、帯の行が
  無い月の下にも1本ずつ入る（部品の作り）。全体の決まり（表のセルは高さ 45px）が当たると、
  割引の無かった月の下にも空の帯が見える。高さは中身どおりにする（中身が無ければ0）。
  請求管理（運営）の一覧で同じ不具合を直したときに、同じ出し方のここも直した。
*/
.billing-history-table::v-deep tbody > tr.p-0 > td.p-0 {
  height: auto;
}

.history-tiers {
  display: grid;
  grid-template-columns: minmax(0, 15rem) auto;
  justify-content: end;
  gap: 0.2rem 1rem;
  margin: 0;
  padding: 0 0.75rem 0.6rem;
  font-size: 0.85rem;
}

.history-tiers dt {
  font-weight: 500;
  color: #768192;
}

.history-tiers dd {
  margin-bottom: 0;
}

/* これまでのご請求の割引額の参考（V33）。ご請求額の下に小さく。合計に入る額と見分けがつくように。 */
.history-discount {
  font-size: 0.8rem;
  color: #768192;
}

.muted {
  color: #768192;
  font-size: 0.85rem;
}

.note {
  font-size: 0.85rem;
  color: #768192;
  margin-bottom: 0.75rem;
}

/* 押せない理由を、ボタンのすぐ上に（離れていると、なぜ押せないのかを結び付けられない）。 */
.save-blocked {
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
  color: #a4353f;
}

/* 請求の対象外の会社に出す1文。欄の中の本文として読める大きさで（注記の小さい字にしない）。 */
.billing-exempt {
  margin-bottom: 0;
  color: #3c4b64;
}

/* お支払い方法を選べない会社に見せる値。入力欄ではないので枠は付けず、欄の値と同じ大きさで出す。 */
.fixed-method-value {
  font-weight: 600;
  color: #3c4b64;
}
.intro-badge { display: inline-block; margin-left: 8px; padding: 1px 8px; border-radius: 10px; background: #e8f4fd; color: #17567a; font-size: 12px; font-weight: 600; }
</style>
